import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM'

type ViewName = 'prompts' | 'video'

export class AngelaGptWidget extends UcoastEl {
	static htmlSelector = 'angela-gpt-widget'

	trigger: HTMLButtonElement
	closeButton: HTMLButtonElement
	backButton: HTMLButtonElement | null
	panel: HTMLElement
	questionButtons: HTMLButtonElement[]
	videoViews: HTMLElement[]
	audioBadge: HTMLButtonElement | null
	audioPlayer: HTMLAudioElement | null
	handleDocumentPointerDownBound: (event: PointerEvent) => void
	handleKeydownBound: (event: KeyboardEvent) => void
	handleAudioEndedBound: () => void

	constructor() {
		super()
		this.trigger = q.rs<HTMLButtonElement>('.angela-gpt-cta__trigger', this)
		this.closeButton = q.rs<HTMLButtonElement>('.angela-gpt-cta__close', this)
		this.backButton = this.querySelector<HTMLButtonElement>('.angela-gpt-cta__back')
		this.panel = q.rs<HTMLElement>('.angela-gpt-cta__panel', this)
		this.questionButtons = Array.from(
			this.querySelectorAll<HTMLButtonElement>('.angela-gpt-cta__question')
		)
		this.videoViews = Array.from(
			this.querySelectorAll<HTMLElement>('.angela-gpt-cta__view--video')
		)
		this.audioBadge = this.querySelector<HTMLButtonElement>('.angela-gpt-cta__audio-badge')
		this.audioPlayer = this.querySelector<HTMLAudioElement>('.angela-gpt-cta__audio-player')
		this.handleDocumentPointerDownBound = this.handleDocumentPointerDown.bind(this)
		this.handleKeydownBound = this.handleKeydown.bind(this)
		this.handleAudioEndedBound = this.handleAudioEnded.bind(this)
	}

	override connectedCallback() {
		super.connectedCallback()
		this.trigger.addEventListener('click', this.toggleOpen)
		this.closeButton.addEventListener('click', this.close)
		this.backButton?.addEventListener('click', this.showPrompts)
		this.questionButtons.forEach((button) => {
			button.addEventListener('click', this.handleQuestionClick)
		})
		this.audioBadge?.addEventListener('click', this.toggleAudio)
		this.audioPlayer?.addEventListener('ended', this.handleAudioEndedBound)
		this.addEventListener('agpt-video-play', this.stopAudio as EventListener)
		document.addEventListener('pointerdown', this.handleDocumentPointerDownBound)
		document.addEventListener('keydown', this.handleKeydownBound)
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		this.trigger.removeEventListener('click', this.toggleOpen)
		this.closeButton.removeEventListener('click', this.close)
		this.backButton?.removeEventListener('click', this.showPrompts)
		this.questionButtons.forEach((button) => {
			button.removeEventListener('click', this.handleQuestionClick)
		})
		this.audioBadge?.removeEventListener('click', this.toggleAudio)
		this.audioPlayer?.removeEventListener('ended', this.handleAudioEndedBound)
		this.removeEventListener('agpt-video-play', this.stopAudio as EventListener)
		document.removeEventListener('pointerdown', this.handleDocumentPointerDownBound)
		document.removeEventListener('keydown', this.handleKeydownBound)
	}

	get isOpen() {
		return this.classList.contains('is-open')
	}

	get currentView(): ViewName {
		const view = this.dataset.view as ViewName | undefined
		return view === 'video' ? 'video' : 'prompts'
	}

	toggleOpen = () => {
		if (this.isOpen) {
			this.close()
			return
		}
		this.open()
	}

	open = () => {
		this.classList.add('is-open')
		this.trigger.setAttribute('aria-expanded', 'true')
		this.panel.setAttribute('aria-hidden', 'false')
		this.setView('prompts', null)
	}

	close = () => {
		this.classList.remove('is-open')
		this.trigger.setAttribute('aria-expanded', 'false')
		this.panel.setAttribute('aria-hidden', 'true')
		this.stopAudio()
		this.resetAllVideos()
		this.setView('prompts', null)
	}

	showPrompts = () => {
		this.setView('prompts', null)
	}

	handleQuestionClick = (event: Event) => {
		const button = event.currentTarget as HTMLButtonElement
		const indexAttr = button.dataset.questionIndex
		if (!indexAttr) return
		const index = Number(indexAttr)
		if (!Number.isFinite(index)) return
		this.setView('video', index)
	}

	setView(view: ViewName, questionIndex: number | null) {
		this.dataset.view = view

		if (view === 'video' && questionIndex !== null) {
			this.dataset.activeQuestion = String(questionIndex)
		} else {
			delete this.dataset.activeQuestion
		}

		if (this.backButton) {
			this.backButton.hidden = view !== 'video'
		}

		if (view !== 'video') {
			this.resetAllVideos()
			return
		}

		this.videoViews.forEach((node) => {
			if (Number(node.dataset.questionIndex) !== questionIndex) {
				const videoEl = node.querySelector<AngelaGptVideo>('angela-gpt-video')
				videoEl?.reset()
			}
		})
	}

	resetAllVideos() {
		this.videoViews.forEach((view) => {
			const videoEl = view.querySelector<AngelaGptVideo>('angela-gpt-video')
			videoEl?.reset()
		})
	}

	toggleAudio = async () => {
		if (!this.audioPlayer || !this.audioBadge) return
		if (this.audioBadge.disabled) return
		if (this.audioPlayer.paused) {
			try {
				await this.audioPlayer.play()
				this.audioBadge.setAttribute('aria-pressed', 'true')
				this.audioBadge.classList.add('is-playing')
			} catch {
				this.stopAudio()
			}
		} else {
			this.stopAudio()
		}
	}

	stopAudio() {
		if (!this.audioPlayer) return
		if (!this.audioPlayer.paused) {
			this.audioPlayer.pause()
		}
		this.audioPlayer.currentTime = 0
		this.audioBadge?.setAttribute('aria-pressed', 'false')
		this.audioBadge?.classList.remove('is-playing')
	}

	handleAudioEnded() {
		this.audioBadge?.setAttribute('aria-pressed', 'false')
		this.audioBadge?.classList.remove('is-playing')
	}

	handleDocumentPointerDown(event: PointerEvent) {
		if (!this.isOpen) return
		const target = q.ot<PointerEvent, HTMLElement>(event)
		if (!target) return
		if (this.contains(target)) return
		this.close()
	}

	handleKeydown(event: KeyboardEvent) {
		if (event.key !== 'Escape' || !this.isOpen) return
		if (this.currentView === 'video') {
			this.showPrompts()
			return
		}
		this.close()
		this.trigger.focus()
	}
}

export class AngelaGptVideo extends UcoastEl {
	static htmlSelector = 'angela-gpt-video'

	poster: HTMLButtonElement | null
	template: HTMLTemplateElement | null

	constructor() {
		super()
		this.poster = this.querySelector<HTMLButtonElement>('.angela-gpt-cta__video-poster')
		this.template = this.querySelector<HTMLTemplateElement>('template')
	}

	override connectedCallback() {
		super.connectedCallback()
		this.poster?.addEventListener('click', this.loadAndPlay)
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		this.poster?.removeEventListener('click', this.loadAndPlay)
	}

	loadAndPlay = () => {
		if (this.dataset.loaded === 'true') return
		if (!this.template) return
		const fragment = this.template.content.cloneNode(true) as DocumentFragment
		this.appendChild(fragment)
		this.dataset.loaded = 'true'
		this.poster?.setAttribute('hidden', '')
		const video = this.querySelector<HTMLVideoElement>('video')
		if (video) {
			video.classList.add('angela-gpt-cta__video-media')
			void video.play().catch(() => {
				// Autoplay may be blocked; user can press the native control.
			})
		}
		this.dispatchEvent(new CustomEvent('agpt-video-play', { bubbles: true }))
	}

	reset() {
		if (this.dataset.loaded !== 'true') return
		const video = this.querySelector<HTMLVideoElement>('video')
		if (video) {
			video.pause()
			video.removeAttribute('src')
			video.load()
			video.remove()
		}
		this.dataset.loaded = 'false'
		this.poster?.removeAttribute('hidden')
	}
}
